import { createDraftSafeSelector } from "@reduxjs/toolkit";
import { resourceExplorerChartLabelsSelector } from "./resourceExplorerChartLabelsSelector";
import { resourceExplorerChartSettingsSelector } from "./settings/resourceExplorerChartSettingsSelector";
import { resourceExplorerWidgetDataSelector } from "../resource-explorer-widget/resourceExplorerWidgetDataSelector";
import { resourceExplorerDatesSelector } from "../data/resourceExplorerDatesSelector";
import { resourceExplorerDataPointSelector } from "../data/resourceExplorerDataPointSelector";
import { resourceExplorerChartBaseSelector } from "../../resourceExplorerChartBaseSelector";
import { resourceExplorerForecastedCostsSelector } from "../forecasted-cost/resourceExplorerForecastedCostsSelector";
import { resourceExplorerShowBudgetLineSelector } from "../data/resourceExplorerShowBudgetLineSelector";
import { resourceExplorerViewCurrentBudgetSelector } from "../../budget/resourceExplorerViewCurrentBudgetSelector";
import { resourceExplorerAxisGranularitySelector } from "../data/resourceExplorerAxisGranularitySelector";
import { addBudgetLineIntoBillingExplorerChartData } from "@/components/pages/common/billing-explorer/utils/addBudgetLineIntoBillingExplorerChartData";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { addForecastedCostIntoBillingExplorerChartData } from "@/components/pages/common/billing-explorer/utils/addForecastedCostIntoBillingExplorerChartData";
import { billingExplorerChartDataGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartDataGenerator";
import { HighchartsChartDataType } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { billingExplorerChartTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/billingExplorerChartTypeSelector";

export const resourceExplorerChartDataSelector = createDraftSafeSelector(
  [
    resourceExplorerWidgetDataSelector,
    resourceExplorerChartLabelsSelector,
    resourceExplorerChartSettingsSelector,
    resourceExplorerDatesSelector,
    organisationQuarterStartMonthSelector,
    resourceExplorerDataPointSelector,
    resourceExplorerChartBaseSelector,
    resourceExplorerForecastedCostsSelector,
    resourceExplorerShowBudgetLineSelector,
    resourceExplorerViewCurrentBudgetSelector,
    resourceExplorerAxisGranularitySelector,
    billingExplorerChartTypeSelector,
  ],
  (
    widgetData,
    labels,
    settings,
    dates,
    quarterStartMonth,
    dataPoint,
    chartBase,
    forecastedCosts,
    showBudgetLine,
    budget,
    xAxisGranularity,
    chartType,
  ): HighchartsChartDataType | null => {
    const data = widgetData?.data;

    if (!data || !labels) {
      return null;
    }

    let chartData = billingExplorerChartDataGenerator(data, labels);

    const needToAddForecast =
      settings?.display_forecast &&
      dataPoint &&
      xAxisGranularity === "date" &&
      chartBase === "cost" &&
      forecastedCosts &&
      chartType !== "pie";

    if (
      needToAddForecast &&
      (dates?.label === "month_to_date" || dates?.label === "quarter_to_date")
    ) {
      const { chartDataWithForecast, labelsWithForecast } =
        addForecastedCostIntoBillingExplorerChartData(
          chartData,
          dates.label,
          dataPoint,
          labels,
          forecastedCosts,
          quarterStartMonth,
        );
      chartData = chartDataWithForecast ?? chartData;
      labels = labelsWithForecast ?? labels;
    }

    if (showBudgetLine && budget && chartType !== "pie") {
      chartData = addBudgetLineIntoBillingExplorerChartData(
        chartData,
        budget,
        labels,
        xAxisGranularity,
        quarterStartMonth,
      );
    }

    return chartData;
  },
);
